import { useCallback, useState } from "react";
import {
  AppModal,
  AppNotificationAlert,
  AppRemixIcon,
  AppTextArea,
} from "@/layout/AppComponents";
import styled from "styled-components";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Formik } from "formik";
import * as Yup from "yup";
import ReactDOMServer from "react-dom/server";
import Moment from "moment";
import { axiosPath } from "../../../../../../../routing/helpers/router";
import Translator from "../../../../../../components/helpers/Translator";
import AppLink from "../../../../../../../routing/helpers/AppLink";
import errorHandler from "@/layout/components/error/ErrorHandler";
import * as currentPageAction from "../../../../../../../store/actions/currentPageAction";
import colors from "../../../../../../../constants/colors";
import useIsMounted from "../../../../../../components/hooks/useIsMounted";
import InformationSelectUsers from "./InformationSelectUsers";
import _ from "lodash";

export default function InformationButtonBlock({ taskID }) {
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const levels = useSelector((state) => state.currentPage?.levels);
  const level1 = useSelector((state) => state.currentPage?.levels?.level1);
  const level2 = useSelector((state) => state.currentPage?.levels?.level2);
  const modulePermissions = useSelector(
    (s) => s.module[moduleBridgeHex].permissions?.correctiveActions
  );
  const moduleJsonSettings = useSelector(
    (s) => s.module[moduleBridgeHex].jsonSettings
  );
  const userBridge = useSelector(
    (state) => state?.module?.[moduleBridgeHex]?.userBridge
  );
  const permissions = useSelector(
    (state) => state?.module[moduleBridgeHex]?.permissions
  );
  const auth = useSelector((state) => state.auth);
  const [deleteInfo, setDeleteInfo] = useState();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCloseRequestModal, setShowCloseRequestModal] = useState(false);
  const [showAddReviewModal, setShowAddReviewModal] = useState(false);
  const [addReviewValidationSchema] = useState(
    Yup.object().shape({
      review: Yup.string()
        .min(0)
        .max(4)
        .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
    })
  );

  const renderAddReviewStars = (setFieldValue, values) => {
    let reviewColor;

    switch (values?.review) {
      case 1:
      case 2:
        reviewColor = colors.danger;
        break;
      case 3:
        reviewColor = colors.state2;
        break;
      case 4:
      case 5:
        reviewColor = colors.success;
        break;
      default:
        reviewColor = colors.grey400;
    }

    return Array.from({ length: 5 }, (_, i) => (
      <ReviewStar
        key={i}
        icon="star"
        size={22}
        color={values?.review >= i + 1 ? reviewColor : colors.grey400}
        filled={values?.review >= i + 1}
        onClick={() => setFieldValue("review", i + 1)}
      />
    ));
  };

  // > CLOSE TASK
  const handleCloseTask = useCallback(
    async () =>
      axios
        .post(axiosPath("api_r_user_module_correctiveActions_task_close"), {
          moduleBridgeHex,
          taskID,
        })
        .then(() => {
          window.location.reload();
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, taskID]
  );
  // < CLOSE TASK

  // > DELETE TASK
  const handleDelete = useCallback(
    async () =>
      axios
        .post(axiosPath("api_r_user_module_correctiveActions_task_delete"), {
          moduleBridgeHex,
          taskID,
        })
        .then((r) => {
          if (isMounted.current) {
            setDeleteInfo(r.data);
            setShowDeleteModal(true);
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, taskID]
  );

  const handleDeleteConfirm = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_correctiveActions_task_deleteConfirm"),
          {
            moduleBridgeHex,
            taskID,
          }
        )
        .then(() => {
          if (isMounted.current) {
            window.location.reload();
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, taskID]
  );
  // < DELETE TASK

  // > CLOSE REQUEST
  const handleCloseRequest = useCallback(
    async (payload) =>
      axios
        .post(
          axiosPath("api_r_user_module_correctiveActions_task_closeRequest"),
          {
            moduleBridgeHex,
            taskID,
            ...payload,
          }
        )
        .then(() => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change("levels", {
                ...levels,
                level2: {
                  ...level2,
                  [taskID]: { ...level2?.[taskID], hasCloseRequest: true },
                },
              })
            );
            AppNotificationAlert({
              title: ReactDOMServer.renderToString(
                <Translator file="module-correctiveActions" name="completed" />
              ),
              message: ReactDOMServer.renderToString(
                <Translator
                  file="module-correctiveActions"
                  name="notify.responsible.review.and.close"
                />
              ),
              type: "success",
              duration: 3000,
              position: "top-right",
            });
            setShowCloseRequestModal(false);
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, taskID]
  );
  // < CLOSE REQUEST

  // > ADD REVIEW
  const handleAddReview = useCallback(
    async (payload) =>
      axios
        .post(
          axiosPath("api_r_user_module_correctiveActions_task_review_add"),
          {
            moduleBridgeHex,
            taskID,
            ...payload,
          }
        )
        .then((r) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change("levels", {
                ...levels,
                level1: {
                  ...level1,
                  [taskID]: {
                    ...level1?.[taskID],
                    reviewValue: r.data?.taskReviewValue,
                    reviewCount: r.data?.reviewCount,
                  },
                },
              })
            );
            AppNotificationAlert({
              title: ReactDOMServer.renderToString(
                <Translator file="module-correctiveActions" name="completed" />
              ),
              message: ReactDOMServer.renderToString(
                <Translator
                  file="module-correctiveActions"
                  name="review.added"
                />
              ),
              type: "success",
              duration: 3000,
              position: "top-right",
            });
            setShowAddReviewModal(false);
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, taskID]
  );
  // < ADD REVIEW

  // > DOWNLOAD DOC EDP REPORT
  const handleReport = () => axios
      .post(
          axiosPath('api_r_user_module_correctiveActions_report_doc_edp'),
          {
            moduleBridgeHex,
            taskID,
          }
      )
      .then(({ data }) => (window.location = data))
      .catch((e) => dispatch(errorHandler(e)))
  // < DOWNLOAD DOC EDP REPORT

  return (
    <Container>
      {(userBridge?.permissions?.level === 0 ||
        userBridge?.id === level2?.[taskID]?.userBridgeID ||
        userBridge?.permissions?.assignedTaskClose) && (
        <button
          type="button"
          className={`btn btn-sm btn-${
            level1?.[taskID]?.closedStamp ? "outline-" : ""
          }success`}
          title={
            level1?.[taskID]?.closedStamp ? "Reabrir tarea" : "Cerrar tarea"
          }
          onClick={handleCloseTask}
        >
          <AppRemixIcon icon="flag" />
        </button>
      )}
      {(userBridge?.permissions?.level === 0 ||
        userBridge?.id === level2?.[taskID]?.userBridgeID ||
        userBridge?.permissions?.ownTaskEdition ||
        level2?.[taskID]?.userHasCAPlant) && (
        <AppLink
          path="user_module_correctiveActions_task_edit"
          params={{
            moduleBridgeHex,
            taskID,
          }}
          className="btn btn-sm btn-primary"
          title={ReactDOMServer.renderToString(
            <Translator file="module-correctiveActions" name="edit.task" />
          )}
        >
          <AppRemixIcon icon="edit-box" />
        </AppLink>
      )}
      {(userBridge?.permissions?.level === 0 ||
        userBridge?.id === level2?.[taskID]?.userBridgeID) && ( // || level2?.[taskID]?.userHasCAPlant
        <button
          type="button"
          className="btn btn-sm btn-danger"
          title={ReactDOMServer.renderToString(
            <Translator file="module-correctiveActions" name="delete.task" />
          )}
          onClick={handleDelete}
        >
          <AppRemixIcon icon="close" />
        </button>
      )}
      {!level1?.[taskID]?.closedStamp && (
        <button
          type="button"
          className="btn btn-sm btn-warning"
          title={ReactDOMServer.renderToString(
            <Translator
              file="module-correctiveActions"
              name="module.ca.close-request"
            />
          )}
          onClick={() => setShowCloseRequestModal(true)}
        >
          <AppRemixIcon icon="close-circle" />
        </button>
      )}
      {userBridge?.userID !== level2?.[taskID]?.userID &&
        modulePermissions?.reviews && (
          <button
            type="button"
            className="btn btn-sm btn-warning"
            title={ReactDOMServer.renderToString(
              <Translator
                file="module-correctiveActions"
                name="module.ca.assess"
              />
            )}
            onClick={() => setShowAddReviewModal(true)}
          >
            <AppRemixIcon icon="star-half" />
          </button>
        )}
      {moduleJsonSettings?.extraReports &&
        moduleJsonSettings?.extraReports === "edpDoc" && (
            <span
                className="btn btn-sm btn-primary"
                title={ReactDOMServer.renderToString(
                    <Translator
                        file="module-correctiveActions"
                        name="download.edp.report"
                    />
                )}
                onClick={handleReport}
            >
                <AppRemixIcon icon="file-word" />
            </span>
        //   <a
        //     target="_blank"
        //     className="btn btn-sm btn-primary"
        //     href={`${global.urlData.userApiUrl}m/corrective-actions/report/doc/edp/${moduleBridgeHex}/${taskID}/`}
        //     rel="noreferrer"
        //     title={ReactDOMServer.renderToString(
        //       <Translator
        //         file="module-correctiveActions"
        //         name="download.edp.report"
        //       />
        //     )}
        //     onClick={() => {}}
        //   >
        //     <AppRemixIcon icon="file-word" />
        //   </a>
        )}
      <AppModal
        show={showDeleteModal}
        title={ReactDOMServer.renderToString(
          <Translator file="module-correctiveActions" name="delete" />
        )}
        isCancelBtn
        isConfirmBtn
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        size="lg"
      >
        <div>
          <Translator
            file="module-correctiveActions"
            name="confirm.delete.task"
          />
        </div>
        <ul>
          {deleteInfo?.taskUpdates > 0 && (
            <li>
              <Translator
                file="module-correctiveActions"
                name="will.delete.number.actions"
                variables={{ actions: deleteInfo?.taskUpdates }}
                isHtml
              />
            </li>
          )}
          {deleteInfo?.taskFiles?.map((f, i) => (
            <li key={i}>
              <Translator
                file="module-correctiveActions"
                name="will.delete.number.task.files"
                variables={{ file: f }}
                isHtml
              />
            </li>
          ))}
          {deleteInfo?.taskUpdateFiles?.map((f, i) => (
            <li key={i}>
              <Translator
                file="module-correctiveActions"
                name="will.delete.number.taskUpdate.files"
                variables={{ file: f }}
                isHtml
              />
            </li>
          ))}
          {deleteInfo?.employees?.map((e, i) => (
            <li key={i}>
              <Translator
                file="module-correctiveActions"
                name="user.will.stop.manage.task"
                variables={{ employee: e }}
                isHtml
              />
            </li>
          ))}
          <li>
            <Translator
              file="module-correctiveActions"
              name="task.will.stop.exsiting"
            />
          </li>
        </ul>
      </AppModal>
      <AppModal
        show={showCloseRequestModal}
        title={ReactDOMServer.renderToString(
          <Translator
            file="module-correctiveActions"
            name="module.ca.close-request"
          />
        )}
        onClose={() => setShowCloseRequestModal(false)}
        size="lg"
      >
        <p className="mb-4">
          <Translator
            file="module-correctiveActions"
            name="module.ca.close-request.send.by"
            variables={{
              name: auth?.user?.name,
              user: auth?.user?.user,
              date: Moment().format("DD/MM/YYYY HH:mm"),
            }}
            isHtml
          />
        </p>
        <div className="alert alert-info p-3">
          <Translator
            file="module-correctiveActions"
            name="module.ca.message.not.registered"
          />
        </div>
        <Formik
          initialValues={{
            message: "",
            users: [],
          }}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={handleCloseRequest}
        >
          {({ setFieldValue, handleSubmit, values, errors, isSubmitting }) => (
            <form onSubmit={handleSubmit}>
              <AppTextArea
                title={`${ReactDOMServer.renderToString(
                  <Translator
                    file="module-correctiveActions"
                    name="module.ca.message"
                  />
                )} (${ReactDOMServer.renderToString(
                  <Translator
                    file="module-correctiveActions"
                    name="module.ca.optional"
                  />
                )})`}
                onChange={(opt) => setFieldValue("message", opt)}
                value={values?.message}
                error={errors?.message}
              />
              {level2?.[taskID].hasCloseRequestSelectUsers &&
                level2?.[taskID].hasCloseRequestSelectUsers === true &&
                permissions.correctiveActions?.closeRequest?.selectUsers &&
                (permissions.correctiveActions?.closeRequest?.selectUsers ===
                  true ||
                  _.isArray(
                    permissions.correctiveActions?.closeRequest?.selectUsers
                  )) && (
                  <InformationSelectUsers
                    values={values}
                    moduleBridgeHex={moduleBridgeHex}
                    clientID={auth?.user?.client?.id}
                    subModuleBridgeHex="0"
                    setFieldValue={setFieldValue}
                    onChange={(opt) => setFieldValue("users", opt)}
                  />
                )}
              <div className="float-right">
                <button
                  type="button"
                  className="btn btn-outline-light mr-2"
                  onClick={() => setShowCloseRequestModal(false)}
                >
                  <Translator file="module-correctiveActions" name="cancel" />
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  <Translator file="module-correctiveActions" name="confirm" />
                </button>
              </div>
            </form>
          )}
        </Formik>
      </AppModal>
      <AppModal
        show={showAddReviewModal}
        title={ReactDOMServer.renderToString(
          <Translator
            file="module-correctiveActions"
            name="initiative.review"
          />
        )}
        onClose={() => setShowAddReviewModal(false)}
        size="lg"
      >
        <Formik
          initialValues={{
            review: undefined,
            message: "",
          }}
          validationSchema={addReviewValidationSchema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={handleAddReview}
        >
          {({ setFieldValue, handleSubmit, values, errors, isSubmitting }) => (
            <form onSubmit={handleSubmit}>
              <div>
                <label>Valoración</label>
                <div className="d-flex">
                  {renderAddReviewStars(setFieldValue, values)}
                </div>
                {errors?.review && (
                  <div className="text-danger">*{errors?.review}</div>
                )}
              </div>
              <AppTextArea
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-correctiveActions"
                    name="leave.your.comments"
                  />
                )}
                icon={<AppRemixIcon icon="chat-4" />}
                onChange={(opt) => setFieldValue("message", opt)}
                value={values?.message}
                error={errors?.message}
              />
              <div className="float-right">
                <button
                  type="button"
                  className="btn btn-outline-light mr-2"
                  onClick={() => setShowAddReviewModal(false)}
                >
                  <Translator file="module-correctiveActions" name="cancel" />
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  <Translator
                    file="module-correctiveActions"
                    name="add.review"
                  />
                </button>
              </div>
            </form>
          )}
        </Formik>
      </AppModal>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  gap: 5px;
  margin-bottom: 10px;
  & > * {
    flex-grow: 1;
    padding: 0.05rem 0.05rem;
  }
`;

const ReviewStar = styled(AppRemixIcon)`
  cursor: pointer;
  margin-left: 5px;
  margin-right: 5px;
`;
